import { FC } from 'react';
import Button from 'jbc-front/components/Button';
import useReactRouter from 'use-react-router';
import { FieldPath, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useEmployeeMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { ServerValidationError, eachValidationErrorsForHookFrom } from '@jbc-year-end-adj/2024/hooks/graphql/error/ServerValidationError';
import { Question } from '@jbc-year-end-adj/2024/features/questions/types';
import { WorkingStudent as WorkingStudentTemplate } from '@jbc-year-end-adj/2024/features/questions/templates/WorkingStudent';
import {
  ButtonContainer,
  ButtonContainer as Buttons
} from '@jbc-year-end-adj/2024/features/questions/components/ButtonContainer/ButtonContainer';
import { Section } from '@jbc-year-end-adj/2024/components/ui/Section';
import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { Textarea } from '@jbc-year-end-adj/2024/components/form/Textarea';
import { FileField } from '@jbc-year-end-adj/2024/components/react-hook-form/FileField';
import { FilePreview } from '@jbc-year-end-adj/2024/features/FilePreview';
import { ANSWER_WORKING_STUDENT } from './mutation';
import { Schema, buildSchema, buildDefaultValues } from './schema';
import { WorkingStudent } from './query';
import { yupResolver } from '@hookform/resolvers/yup';
import { ImageUploadSettings } from '@jbc-year-end-adj/2024/types';

type WorkingStudentProps = {
  question: Question;
  imageUploadSetting: ImageUploadSettings;
  workingStudent: WorkingStudent;
};

export const Form: FC<WorkingStudentProps> = ({ question, imageUploadSetting, workingStudent }) => {
  const imageRequired = imageUploadSetting === 'required';
  const methods = useForm<Schema>({
    resolver: yupResolver(buildSchema(imageRequired)),
    defaultValues: buildDefaultValues(workingStudent)
  });
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors }
  } = methods;

  const { history } = useReactRouter();
  const [answer, { loading: loadingAnswer }] = useEmployeeMutation(ANSWER_WORKING_STUDENT);

  const handleAnswerNoneWorkingStudent = async () => {
    await answer({
      variables: {
        input: {
          classification: 'none',
          detail: null,
          image: null
        }
      }
    });

    history.push('/employee_input');
  };

  const onSubmit: SubmitHandler<Schema> = async data => {
    try {
      await answer({
        variables: {
          input: {
            classification: data.classification,
            detail: data.detail,
            image: data.image
          }
        }
      });
      history.push('/employee_input');
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, buildSchema(imageRequired), data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  const classification = watch('classification');
  const image = watch('image');

  return (
    <FormProvider {...methods}>
      <WorkingStudentTemplate>
        <WorkingStudentTemplate.CheckWorkingStudent question={question}>
          <Buttons>
            <Button
              huge
              primary={classification === 'working_student'}
              secondary={classification !== 'working_student'}
              disabled={loadingAnswer}
              onClick={() => setValue('classification', 'working_student')}
            >
              はい
            </Button>
            <Button
              huge
              primary={classification === 'none'}
              secondary={classification !== 'none'}
              disabled={loadingAnswer}
              onClick={() => handleAnswerNoneWorkingStudent()}
            >
              いいえ
            </Button>
          </Buttons>
        </WorkingStudentTemplate.CheckWorkingStudent>
        {classification === 'working_student' && (
          <WorkingStudentTemplate.DetailInput>
            <Section>
              <Section.Header>勤労学生区分</Section.Header>
              <Section.Body>
                <FormSection>
                  <Textarea label="勤労学生詳細情報" note="学校名と入学年月日を記入してください。" {...register('detail')} />
                  {imageUploadSetting !== 'none' && (
                    <FileField
                      label="学生手帳画像"
                      name="image"
                      required={imageRequired}
                      error={errors.image?.message}
                      isError={!!errors.image?.message}
                    >
                      {image && (
                        <FileField.FileContainer>
                          <FileField.File file={image} onRemove={() => setValue(`image`, null)} />
                          <FilePreview file={image} />
                        </FileField.FileContainer>
                      )}
                    </FileField>
                  )}
                </FormSection>
              </Section.Body>
            </Section>
            <ButtonContainer>
              <Button primary disabled={loadingAnswer} onClick={handleSubmit(onSubmit)}>
                次へ
              </Button>
            </ButtonContainer>
          </WorkingStudentTemplate.DetailInput>
        )}
      </WorkingStudentTemplate>
    </FormProvider>
  );
};
